import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
        Accessibility, 
        ZoomIn, 
        ZoomOut, 
        Eye, 
        Contrast,
        Type,
        X,
        RotateCcw,
        BookOpen
      } from 'lucide-react';

export default function AccessibilityWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [fontSize, setFontSize] = useState(100);
    const [highContrast, setHighContrast] = useState(false);
    const [grayscale, setGrayscale] = useState(false);
    const [underlineLinks, setUnderlineLinks] = useState(false);
    const [dyslexiaFont, setDyslexiaFont] = useState(false);

  useEffect(() => {
    // Apply font size
    document.documentElement.style.fontSize = `${fontSize}%`;
    
    // Apply high contrast
    if (highContrast) {
      document.documentElement.classList.add('high-contrast');
    } else {
      document.documentElement.classList.remove('high-contrast');
    }

    // Apply grayscale
    if (grayscale) {
      document.documentElement.classList.add('grayscale-mode');
    } else {
      document.documentElement.classList.remove('grayscale-mode');
    }

    // Apply underline links
    if (underlineLinks) {
      document.documentElement.classList.add('underline-links');
    } else {
      document.documentElement.classList.remove('underline-links');
    }

    // Apply dyslexia-friendly font
    if (dyslexiaFont) {
      document.documentElement.classList.add('dyslexia-font');
    } else {
      document.documentElement.classList.remove('dyslexia-font');
    }

    // Save preferences to localStorage
    localStorage.setItem('accessibility-preferences', JSON.stringify({
      fontSize,
      highContrast,
      grayscale,
      underlineLinks,
      dyslexiaFont
    }));
    }, [fontSize, highContrast, grayscale, underlineLinks, dyslexiaFont]);

  useEffect(() => {
    // Load preferences from localStorage
    const saved = localStorage.getItem('accessibility-preferences');
    if (saved) {
      try {
        const prefs = JSON.parse(saved);
            setFontSize(prefs.fontSize || 100);
            setHighContrast(prefs.highContrast || false);
            setGrayscale(prefs.grayscale || false);
            setUnderlineLinks(prefs.underlineLinks || false);
            setDyslexiaFont(prefs.dyslexiaFont || false);
      } catch (e) {
        console.error('Error loading accessibility preferences', e);
      }
    }
  }, []);

  const increaseFontSize = () => {
    if (fontSize < 150) setFontSize(fontSize + 10);
  };

  const decreaseFontSize = () => {
    if (fontSize > 80) setFontSize(fontSize - 10);
  };

  const resetAll = () => {
    setFontSize(100);
    setHighContrast(false);
    setGrayscale(false);
    setUnderlineLinks(false);
    setDyslexiaFont(false);
  };

  return (
    <>
      {/* Floating Accessibility Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 left-6 z-50 w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 focus:outline-none focus:ring-4 focus:ring-blue-300"
        aria-label="Open Accessibility Menu"
        title="Accessibility Options"
      >
        <Accessibility className="w-7 h-7" />
      </button>

      {/* Accessibility Panel */}
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={() => setIsOpen(false)}
            />
            <motion.div
              initial={{ x: 400, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 400, opacity: 0 }}
              transition={{ type: 'spring', damping: 25 }}
              className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl z-50 overflow-y-auto"
            >
              <Card className="border-0 rounded-none h-full">
                <CardHeader className="border-b bg-blue-600 text-white">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Accessibility className="w-6 h-6" />
                      <CardTitle className="text-white">Accessibility Options</CardTitle>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setIsOpen(false)}
                      className="text-white hover:bg-blue-700"
                      aria-label="Close Accessibility Menu"
                    >
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  {/* Font Size */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Type className="w-5 h-5 text-blue-600" />
                      <h3 className="font-semibold text-slate-800">Text Size</h3>
                    </div>
                    <div className="flex items-center gap-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={decreaseFontSize}
                        disabled={fontSize <= 80}
                        className="flex-shrink-0"
                        aria-label="Decrease text size"
                      >
                        <ZoomOut className="w-4 h-4" />
                      </Button>
                      <div className="flex-1 text-center">
                        <span className="text-lg font-medium">{fontSize}%</span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={increaseFontSize}
                        disabled={fontSize >= 150}
                        className="flex-shrink-0"
                        aria-label="Increase text size"
                      >
                        <ZoomIn className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* High Contrast */}
                  <div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Contrast className="w-5 h-5 text-blue-600" />
                        <h3 className="font-semibold text-slate-800">High Contrast</h3>
                      </div>
                      <button
                        onClick={() => setHighContrast(!highContrast)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          highContrast ? 'bg-blue-600' : 'bg-slate-300'
                        }`}
                        role="switch"
                        aria-checked={highContrast}
                        aria-label="Toggle high contrast mode"
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            highContrast ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    <p className="text-sm text-slate-600 mt-2">
                      Increases contrast for better visibility
                    </p>
                  </div>

                  {/* Grayscale */}
                  <div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Eye className="w-5 h-5 text-blue-600" />
                        <h3 className="font-semibold text-slate-800">Grayscale Mode</h3>
                      </div>
                      <button
                        onClick={() => setGrayscale(!grayscale)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          grayscale ? 'bg-blue-600' : 'bg-slate-300'
                        }`}
                        role="switch"
                        aria-checked={grayscale}
                        aria-label="Toggle grayscale mode"
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            grayscale ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    <p className="text-sm text-slate-600 mt-2">
                      Removes colors for color-blind users
                    </p>
                  </div>

                  {/* Underline Links */}
                  <div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Type className="w-5 h-5 text-blue-600" />
                        <h3 className="font-semibold text-slate-800">Underline Links</h3>
                      </div>
                      <button
                        onClick={() => setUnderlineLinks(!underlineLinks)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          underlineLinks ? 'bg-blue-600' : 'bg-slate-300'
                        }`}
                        role="switch"
                        aria-checked={underlineLinks}
                        aria-label="Toggle underlined links"
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            underlineLinks ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    <p className="text-sm text-slate-600 mt-2">
                      Makes links more visible
                    </p>
                  </div>

                  {/* Dyslexia-Friendly Font */}
                  <div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <BookOpen className="w-5 h-5 text-blue-600" />
                        <h3 className="font-semibold text-slate-800">Dyslexia Font</h3>
                      </div>
                      <button
                        onClick={() => setDyslexiaFont(!dyslexiaFont)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          dyslexiaFont ? 'bg-blue-600' : 'bg-slate-300'
                        }`}
                        role="switch"
                        aria-checked={dyslexiaFont}
                        aria-label="Toggle dyslexia-friendly font"
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            dyslexiaFont ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    <p className="text-sm text-slate-600 mt-2">
                      Uses OpenDyslexic font for easier reading
                    </p>
                  </div>

                  {/* Reset Button */}
                  <div className="pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={resetAll}
                      className="w-full"
                      aria-label="Reset all accessibility settings"
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Reset All Settings
                    </Button>
                  </div>

                  {/* Info */}
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <h4 className="font-semibold text-blue-900 mb-2">Keyboard Navigation</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• Press <kbd className="px-1 bg-white border rounded">Tab</kbd> to navigate</li>
                      <li>• Press <kbd className="px-1 bg-white border rounded">Enter</kbd> to activate</li>
                      <li>• Press <kbd className="px-1 bg-white border rounded">Esc</kbd> to close menus</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Global Styles. The OpenDyslexic stylesheet is only requested once the
          visitor turns the dyslexia-friendly font on; it used to be fetched on
          every page load for everyone. */}
      <style>{`
        ${dyslexiaFont ? "@import url('https://fonts.googleapis.com/css2?family=OpenDyslexic:wght@400;700&display=swap');" : ''}

        .high-contrast {
          filter: contrast(1.5);
        }

        .grayscale-mode {
          filter: grayscale(100%);
        }

        .underline-links a {
          text-decoration: underline !important;
        }

        .dyslexia-font {
          font-family: 'OpenDyslexic', sans-serif !important;
        }

        .dyslexia-font * {
          font-family: 'OpenDyslexic', sans-serif !important;
        }

        /* Focus visible styles for keyboard navigation */
        *:focus-visible {
          outline: 3px solid #3b82f6 !important;
          outline-offset: 2px !important;
        }

        /* Skip to main content link */
        .skip-to-main {
          position: absolute;
          left: -9999px;
          z-index: 999;
          padding: 1em;
          background-color: #3b82f6;
          color: white;
          text-decoration: none;
          border-radius: 0.5rem;
        }

        .skip-to-main:focus {
          left: 50%;
          top: 1rem;
          transform: translateX(-50%);
        }
      `}</style>
    </>
  );
}